'use client';

import {CheckIcon, TriangleDownIcon} from '@radix-ui/react-icons';
import * as SelectPrimitive from '@radix-ui/react-select';
import clsx from 'clsx';
import {type ComponentPropsWithoutRef, forwardRef, useState} from 'react';
import styles from './SelectDropDown.module.scss';

type SelectProps = ComponentPropsWithoutRef<typeof SelectPrimitive.Root> & {
  placeholder?: string;
  label?: string;
  required?: boolean;
  invalid?: boolean;
  errorText?: string | undefined;
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  onChange?: (...event: any[]) => void;
  cntstyleprop?: string;
  vpstyleprop?: string;
  className?: string;
  secondaryBackground?: boolean;
  disabled?: boolean;
  tertiaryBackground?: boolean;
};

export const SelectDropDown = forwardRef<HTMLButtonElement, SelectProps>(
  (
    {
      children,
      placeholder,
      label,
      required,
      invalid,
      errorText,
      onChange,
      cntstyleprop,
      vpstyleprop,
      className,
      disabled,
      ...props
    },
    forwardedRef,
  ): React.JSX.Element => {
    const [isOpen, setIsOpen] = useState<boolean>(false);

    const handleOpenChange = (open: boolean) => {
      setIsOpen(!!open);
    };

    return (
      <div className={styles.wrapper}>
        <div className={clsx(className, styles.selectContainer)}>
          <SelectPrimitive.Root
            {...props}
            defaultValue={props.defaultValue}
            value={props.value}
            onValueChange={(value: string) => onChange?.(value)}
            open={isOpen}
            onOpenChange={handleOpenChange}
            required={required || false}
          >
            <SelectPrimitive.Trigger
              className={clsx(
                styles.menuTrigger,
                props.value && styles.highlightColor,
                isOpen && props.value && styles.openColor,
                invalid && styles.hasError,
                disabled && styles.disabled,
              )}
              ref={forwardedRef}
              aria-labelledby="dropdown-label"
            >
              <label
                id="dropdown-label"
                className={clsx(
                  styles.textFieldLabel,
                  props.secondaryBackground && styles.secondaryBackground,
                  props.tertiaryBackground && styles.tertiaryBackground,
                )}
              >
                {label}
              </label>

              {props.value || props.defaultValue ? (
                <SelectPrimitive.Value />
              ) : (
                <span>{!invalid ? placeholder : ''}</span>
              )}

              <SelectPrimitive.Icon className={styles.menuIcon}>
                <TriangleDownIcon />
              </SelectPrimitive.Icon>
            </SelectPrimitive.Trigger>
            <SelectPrimitive.Portal>
              <SelectPrimitive.Content
                position="popper"
                className={clsx(styles.menuContent, cntstyleprop)}
                sideOffset={props.value ? 5 : 0}
              >
                <SelectPrimitive.Viewport
                  className={clsx(
                    styles.Viewport,
                    props.value && styles.highlightColor,
                    vpstyleprop,
                  )}
                >
                  {children}
                </SelectPrimitive.Viewport>
              </SelectPrimitive.Content>
            </SelectPrimitive.Portal>
          </SelectPrimitive.Root>
        </div>
        {<span className={styles.errorText}>{errorText}</span>}
      </div>
    );
  },
);

type SelectItemProps = ComponentPropsWithoutRef<typeof SelectPrimitive.Item> & {
  indstyleprop?: string;
  itemstyleprop?: string;
};

export const SelectItemDropDown = forwardRef<HTMLDivElement, SelectItemProps>(
  ({children, className, itemstyleprop, indstyleprop, ...props}, ref) => {
    return (
      <SelectPrimitive.Item
        {...props}
        className={clsx(styles.menuItem, className)}
        ref={ref}
      >
        <SelectPrimitive.ItemText className={itemstyleprop}>
          {children}
        </SelectPrimitive.ItemText>
        <SelectPrimitive.ItemIndicator
          className={clsx(styles.ItemIndicator, indstyleprop)}
        >
          <CheckIcon className={clsx(styles.iconSize)} />
        </SelectPrimitive.ItemIndicator>
      </SelectPrimitive.Item>
    );
  },
);
